import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API_ENDPOINTS, ADMIN_ENDPOINTS } from "../../config/constants";
import confetti from "canvas-confetti";
import {
  isG80Car,
  vehicleBodyStyles,
  vehicleColors,
} from "../../constants/vehicleConstants";
import DiscussionThread from "../Discussion";
import VehicleTags from "../VehicleTags";
import { useVehicleState } from "./hooks/useVehicleState";
import { useVehicleImages } from "./hooks/useVehicleImages";
import VehicleImages from "./components/VehicleImages";
import BasicInfo from "./components/BasicInfo";
import ProfileSection from "./components/ProfileSection";
import ChangeHistory from "./components/ChangeHistory";
import UserAttributionPill from "./components/UserAttributionPill";
import { formatDate } from "./utils/formatDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShare,
  faCopy,
  faCamera,
  faEye,
  faTrash,
  faSpinner,
  faUndo,
  faExclamationTriangle,
  faFire,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import MetaTags from "../MetaTags";
import { Link, useNavigate } from "react-router-dom";
import LikeButton from "../LikeButton";
import PlateDisplay from "../PlateDisplay";
import ProfileRichnessIndicator from "../ProfileRichnessIndicator";
import ScoreChangePopup from "../ScoreChangePopup";

const VehicleProfile = ({ vehicle, user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const vehicleState = useVehicleState(vehicle);
  const imageState = useVehicleImages(vehicle);
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const [deletionTimer, setDeletionTimer] = useState(60);
  const [isDeletingVehicle, setIsDeletingVehicle] = useState(false);
  const [isRestoringVehicle, setIsRestoringVehicle] = useState(false);
  const [scorePopup, setScorePopup] = useState(null);
  const [currentScore, setCurrentScore] = useState(
    vehicle.profileRichnessScore || 0
  );

  // Add local state to manage vehicle data, including deletion status
  const [localVehicle, setLocalVehicle] = useState(vehicle);

  // Keep localVehicle in sync with parent vehicle prop
  useEffect(() => {
    setLocalVehicle(vehicle);
  }, [vehicle]);

  // Reset all vehicle state values when vehicle changes
  useEffect(() => {
    if (vehicle) {
      // Reset all state values to match the new vehicle
      vehicleState.setMake(vehicle.basicInfo?.make || "");
      vehicleState.setModel(vehicle.basicInfo?.model || "");
      vehicleState.setColor(vehicle.color || "");
      vehicleState.setBodyStyle(vehicle.bodyStyle || "");
      vehicleState.setG80ConversionKit(vehicle.g80ConversionKit || "");
      vehicleState.setProfile(vehicle.profile || "");
      vehicleState.setChangeHistory(vehicle.changeHistory || []);
      vehicleState.setBasicInfoMessage(null);
      vehicleState.setProfileMessage(null);
    }
  }, [vehicle]);

  // Determine user permissions
  const isCreator = user && localVehicle?.firstAddedBy?._id === user._id;
  const isAdmin = user?.isAdmin === true;
  const canHardDelete = isCreator && deletionTimer > 0;
  const canSoftDelete = (isCreator || isAdmin) && !localVehicle?.isDeleted;
  const canRestore = isAdmin && localVehicle?.isDeleted;

  // Handle confetti, image prompt, and deletion timer for newly found plates
  useEffect(() => {
    // Check if this is a newly found plate by the current user (within 5 seconds)
    const isNewlyFoundByCurrentUser =
      localVehicle &&
      !localVehicle.isDeleted && // Only run if not already deleted
      new Date().getTime() - new Date(localVehicle.firstAddedAt).getTime() <
        5000 &&
      isCreator;

    if (isNewlyFoundByCurrentUser) {
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        styles: {
          pointerEvents: "none",
        },
      });

      // Show image upload prompt
      setShowImagePrompt(true);

      // Start deletion timer
      setDeletionTimer(60); // Reset timer to 60 when modal is shown
      const interval = setInterval(() => {
        setDeletionTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Cleanup function to clear interval if component unmounts or deps change
      return () => clearInterval(interval);
    } else {
      // If not newly found by current user, ensure timer is 0 and prompt is hidden
      setDeletionTimer(0);
      setShowImagePrompt(false);
    }
    // Dependencies: Run when vehicle data, user, or creator status changes
  }, [localVehicle, user, isCreator]);

  // General delete handler (calls backend which decides hard/soft)
  const handleDeleteVehicle = useCallback(async () => {
    if (!isCreator && !isAdmin) {
      toast.error(t("vehicle.delete.no_permission"));
      return;
    }

    const confirmMessage = localVehicle.isDeleted
      ? "This vehicle is already deleted." // Should not happen
      : canHardDelete
      ? t("vehicle.delete.confirm_hard")
      : t("vehicle.delete.confirm_soft");

    const confirmDelete = window.confirm(confirmMessage);
    if (!confirmDelete) return;

    const isHardDeleteAttempt = canHardDelete; // Store if this was a hard delete attempt

    try {
      setIsDeletingVehicle(true);
      const response = await fetch(
        API_ENDPOINTS.DELETE_VEHICLE(localVehicle._id),
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t("vehicle.delete.error"));
      }

      toast.success(result.message || t("vehicle.delete.success"));

      // <<< --- FIX: Adjust navigation/state update --- >>>
      if (isHardDeleteAttempt && response.ok) {
        // If it was a hard delete attempt and successful, navigate away
        navigate("/");
      } else if (!isHardDeleteAttempt && response.ok) {
        // If it was a soft delete attempt and successful, update local state
        setLocalVehicle((prev) => ({
          ...prev,
          isDeleted: true,
          deletedAt: new Date(),
        }));
        setIsDeletingVehicle(false); // Reset loading state as we stay on the page
      } else {
        // Handle cases where response was ok but maybe didn't match expectation?
        // Or just reset loading state on error (handled below)
        setIsDeletingVehicle(false);
      }
    } catch (error) {
      toast.error(error.message);
      setIsDeletingVehicle(false); // Ensure loading state is reset on error
    }
  }, [localVehicle, user, navigate, t, isCreator, isAdmin, canHardDelete]);

  // Restore handler (only for admins)
  const handleRestoreVehicle = useCallback(async () => {
    if (!isAdmin) {
      toast.error("You are not authorized to restore this vehicle.");
      return;
    }

    const confirmRestore = window.confirm(t("vehicle.restore.confirm"));
    if (!confirmRestore) return;

    try {
      setIsRestoringVehicle(true);
      const response = await fetch(
        ADMIN_ENDPOINTS.RESTORE_VEHICLE(localVehicle._id),
        {
          method: "POST",
          credentials: "include",
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t("vehicle.restore.error"));
      }

      toast.success(result.message || t("vehicle.restore.success"));

      // Update local state to reflect restoration
      setLocalVehicle((prev) => ({
        ...prev,
        isDeleted: false,
        deletedAt: undefined,
        deletedBy: undefined,
      }));
      // Fetch fresh data or rely on cache invalidation? For now, just update local state.
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsRestoringVehicle(false);
    }
  }, [localVehicle, navigate, t, isAdmin, ADMIN_ENDPOINTS]);

  const handleImagePromptResponse = (wantsToUpload) => {
    setShowImagePrompt(false);
    if (wantsToUpload) {
      // First show the image upload form
      imageState.setShowImageForm(true);
      // Use a small delay to ensure the form is rendered before trying to click
      setTimeout(() => {
        const dropzone = document.querySelector('input[type="file"]');
        if (dropzone) {
          dropzone.click();
        }
      }, 100);
    }
  };

  // Convert arrays to react-select options
  const makeOptions = vehicleState.makes.map((m) => ({
    value: m.name,
    label: m.name,
  }));
  const modelOptions =
    vehicleState.makes
      .find((m) => m.name === vehicleState.make)
      ?.models.filter((m) => m.isActive)
      .map((m) => ({ value: m.name, label: m.name })) || [];
  const colorOptions = vehicleColors.map((c) => ({
    value: c,
    label: c,
  }));
  const bodyStyleOptions = vehicleBodyStyles.map((b) => ({
    value: b,
    label: b,
  }));
  const g80Options = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];

  // Add clipboard monitoring
  useEffect(() => {
    if (!user) return; // Only monitor clipboard for logged-in users

    const handlePaste = (e) => {
      const text = e.clipboardData.getData("text");
      if (!text) return;

      try {
        const url = new URL(text);
        const hasImageExtension = /\.(jpg|jpeg|png|gif)$/i.test(url.pathname);

        if (hasImageExtension && imageState.showImageForm) {
          imageState.setImageUrl(text);
          imageState.setImageMessage(
            "Image URL detected from clipboard! Click Submit to upload."
          );
        }
      } catch (err) {
        // Not a valid URL, ignore
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [user, imageState.showImageForm]);

  // Function to handle score changes
  const handleScoreChange = (newScore, clickEvent) => {
    const scoreDiff = newScore - currentScore;
    if (scoreDiff !== 0) {
      setScorePopup({
        scoreChange: scoreDiff,
        position: {
          x: clickEvent?.clientX || window.innerWidth / 2,
          y: clickEvent?.clientY || window.innerHeight / 2,
        },
      });
      setCurrentScore(newScore);

      // Clear popup after animation
      setTimeout(() => {
        setScorePopup(null);
      }, 1100); // Slightly longer than animation duration
    }
  };

  const handleBasicInfoSave = async (e) => {
    if (!user) {
      vehicleState.setBasicInfoMessage({
        text: t("vehicle.basic_info.login_required"),
        type: "error",
      });
      return;
    }
    try {
      const res = await fetch(`/api/vehicles/${vehicle._id}/basic-info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          make: vehicleState.make,
          model: vehicleState.model,
          color: vehicleState.color,
          bodyStyle: vehicleState.bodyStyle,
          g80ConversionKit: vehicleState.g80ConversionKit,
        }),
      });
      const updated = await res.json();
      vehicleState.setMake(updated.basicInfo.make);
      vehicleState.setModel(updated.basicInfo.model);
      vehicleState.setColor(updated.color);
      vehicleState.setBodyStyle(updated.bodyStyle);
      vehicleState.setChangeHistory(updated.changeHistory);
      handleScoreChange(updated.profileRichnessScore, e);
      vehicleState.setBasicInfoMessage({
        text: t("vehicle.basic_info.success"),
        type: "success",
      });
    } catch (err) {
      console.error("Error updating basic info", err);
      vehicleState.setBasicInfoMessage({
        text: t("vehicle.basic_info.error"),
        type: "error",
      });
    }
  };

  const handleProfileSave = async (e) => {
    if (!user) {
      vehicleState.setProfileMessage({
        text: t("vehicle.basic_info.login_required"),
        type: "error",
      });
      return;
    }
    try {
      const res = await fetch(
        `${API_ENDPOINTS.VEHICLES}/${vehicle._id}/profile`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ profile: vehicleState.profile }),
          credentials: "include",
        }
      );
      const updated = await res.json();
      vehicleState.setProfile(updated.profile);
      vehicleState.setChangeHistory(updated.changeHistory);
      handleScoreChange(updated.profileRichnessScore, e);
      vehicleState.setProfileMessage({
        text: t("vehicle.profile.success"),
        type: "success",
      });
    } catch (err) {
      console.error("Error updating profile", err);
      vehicleState.setProfileMessage({
        text: t("vehicle.profile.error"),
        type: "error",
      });
    }
  };

  const handleSubmitImage = async (e) => {
    e.preventDefault();
    const validExtensions = [
      // Images
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".bmp",
      ".tiff",
      ".svg",
      ".heic",
      ".heif",
      // Videos
      ".mp4",
      ".webm",
      ".mov",
      ".avi",
    ];
    const lowerUrl = imageState.imageUrl.toLowerCase();
    const isValid = validExtensions.some((ext) => lowerUrl.endsWith(ext));
    if (!isValid) {
      imageState.setImageMessage(
        "Invalid file URL. Please ensure it ends with a supported image or video format."
      );
      return;
    }

    try {
      const res = await fetch(API_ENDPOINTS.SUBMIT_VEHICLE_IMAGE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          vehicleId: vehicle._id,
          imageUrl: imageState.imageUrl,
          isPrimary: imageState.approvedImages.length === 0,
          isVideo: /\.(mp4|webm|mov|avi)$/i.test(lowerUrl),
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to submit image");
      }

      const updatedVehicle = await res.json();
      // Update the local state with the new image
      imageState.setApprovedImages(
        updatedVehicle.images.filter((img) => img.approvedAt)
      );
      handleScoreChange(updatedVehicle.profileRichnessScore, e);
      imageState.setImageUrl("");
      imageState.setImageMessage("Media added successfully!");
      imageState.setShowImageForm(false);
    } catch (err) {
      console.error("Error submitting media:", err);
      imageState.setImageMessage("Error submitting media. Please try again.");
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: `${vehicle.plate} - G80 Or Not?`,
        text: `Check out this vehicle on G80 Or Not!`,
        url: window.location.href,
      });
    } catch (err) {
      // If Web Share API is not supported, fallback to copying the URL
      handleCopyLink();
    }
  };

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      toast.success(t("vehicle.share.copied"));
    } catch (err) {
      toast.error(t("vehicle.share.error"));
    }
  };

  const hasG80Badge =
    vehicle.basicInfo &&
    isG80Car(
      vehicle.basicInfo.make,
      vehicle.basicInfo.model,
      vehicle.g80ConversionKit === "Yes"
    );
  const hasG80Kit = vehicle.g80ConversionKit === "Yes";

  const getMetaDescription = () => {
    const parts = [];

    // Add basic vehicle info
    const basicInfo = `${vehicle.basicInfo.make || ""} ${
      vehicle.basicInfo.model || ""
    }`.trim();
    if (basicInfo) parts.push(basicInfo);

    // Add G80 status
    if (hasG80Badge) {
      parts.push("G80 Verified");
    } else if (hasG80Kit) {
      parts.push("G80 Kit");
    }

    // Add profile excerpt if available
    if (vehicle.profile) {
      parts.push(
        vehicle.profile.substring(0, 150) +
          (vehicle.profile.length > 150 ? "..." : "")
      );
    }

    return parts.join(" | ") || "Check out this vehicle on G80 or Not!";
  };

  const getMetaImage = () => {
    if (!vehicle.images?.length) return "/favicon/android-chrome-512x512.png";

    const imageUrl = vehicle.images[0].url;

    // If it's already an absolute URL, return as is
    if (imageUrl.startsWith("http")) return imageUrl;

    // Otherwise, make it absolute using the current origin
    return `${window.location.origin}${imageUrl}`;
  };

  const handleBidAction = async (actionType) => {
    // implementation
  };

  // Helper function to check if a streak day is a milestone and get confetti colors
  const getStreakMilestoneConfig = (days) => {
    const isSpecialMilestone =
      t(`vehicle.streak_milestones.${days}`, "") !== "";

    if (!isSpecialMilestone) return null;

    // Determine confetti colors and settings based on milestone
    let colors = ["#ff0000", "#ffa500"]; // Default fire colors
    let particleCount = 100;
    let spread = 70;

    if (days === 7) {
      // One week - fire colors
      colors = ["#ff0000", "#ffa500", "#ffff00"];
      particleCount = 100;
    } else if (days === 14) {
      // Two weeks - blue/green colors
      colors = ["#00ff00", "#00ffff", "#0000ff"];
      particleCount = 120;
    } else if (days === 30) {
      // One month - rainbow colors
      colors = [
        "#ff0000",
        "#ffa500",
        "#ffff00",
        "#00ff00",
        "#00ffff",
        "#0000ff",
        "#ff00ff",
      ];
      particleCount = 140;
    } else if (days === 60) {
      // Two months - silver/blue colors
      colors = ["#c0c0c0", "#a9a9a9", "#4169e1", "#00bfff"];
      particleCount = 150;
    } else if (days === 69) {
      // Nice
      colors = ["#ff00ff", "#ff1493", "#ff69b4"];
      particleCount = 169;
    } else if (days === 90) {
      // Three months - gold/bronze colors
      colors = ["#ffd700", "#daa520", "#cd7f32"];
      particleCount = 160;
    } else if (days === 100) {
      // Century - premium gold/white
      colors = ["#ffd700", "#ffdf00", "#f0f8ff", "#ffffff"];
      particleCount = 180;
    } else if (days === 365) {
      // One year - platinum colors
      colors = ["#e5e4e2", "#ffffff", "#ffd700", "#ff00ff"];
      particleCount = 225;
      spread = 100;
    }

    return { colors, particleCount, spread, isSpecialMilestone };
  };

  const stripedMakeModel = () => {
    // implementation
  };

  const handleTagsUpdate = (updatedVehicle, event) => {
    // Update both the change history and the vehicle data
    vehicleState.setChangeHistory(updatedVehicle.changeHistory);
    // Update the local vehicle state
    setLocalVehicle(updatedVehicle);
    // Show score change popup
    handleScoreChange(updatedVehicle.profileRichnessScore, event);
  };

  return (
    <div className="relative">
      {/* Image upload prompt modal with Timed Delete Button */}
      {showImagePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md mx-4 dark:text-gray-400">
            <h3 className="text-xl font-bold mb-4">
              {t("vehicle.images.prompt_title")}
            </h3>
            <p className="text-sm mb-4">{t("vehicle.images.prompt")}</p>
            {user?.spotterStats?.currentStreak?.days > 0 && (
              <div
                className={`mb-4 p-3 ${
                  getStreakMilestoneConfig(user.spotterStats.currentStreak.days)
                    ? "bg-gradient-to-r from-amber-100 to-yellow-100 border border-yellow-300 rounded-lg shadow-md animate-pulse dark:bg-gray-700 dark:border-gray-600"
                    : "bg-green-50 border border-green-200 rounded-lg dark:bg-gray-700 dark:border-gray-600"
                }`}
              >
                <div className="flex items-center gap-2 text-green-700 dark:text-gray-400">
                  <FontAwesomeIcon
                    icon={faFire}
                    className={`${
                      getStreakMilestoneConfig(
                        user.spotterStats.currentStreak.days
                      )
                        ? "text-amber-500 text-xl animate-bounce dark:text-gray-400"
                        : "text-green-500 dark:text-gray-400"
                    }`}
                  />
                  <span
                    className={`${
                      getStreakMilestoneConfig(
                        user.spotterStats.currentStreak.days
                      )
                        ? "font-bold text-amber-700 dark:text-gray-400"
                        : "font-medium dark:text-gray-400"
                    }`}
                  >
                    {(() => {
                      const days = user.spotterStats.currentStreak.days;

                      // Check if we have a milestone message for this streak count
                      const milestoneConfig = getStreakMilestoneConfig(days);

                      if (milestoneConfig?.isSpecialMilestone) {
                        // Trigger confetti for milestone streaks
                        setTimeout(() => {
                          confetti({
                            particleCount: milestoneConfig.particleCount,
                            spread: milestoneConfig.spread,
                            colors: milestoneConfig.colors,
                            origin: { y: 0.6 },
                          });
                        }, 300);

                        return t(`vehicle.streak_milestones.${days}`);
                      }

                      // If not a milestone, use random variation or default message
                      const variations = t("vehicle.streak_variations", []);
                      if (Array.isArray(variations) && variations.length > 0) {
                        // Use a random variation based on the day to keep it consistent
                        const randomIndex = days % variations.length;
                        return variations[randomIndex];
                      }

                      // Fallback to the default message
                      return t("vehicle.streak_extended", {
                        days,
                      });
                    })()}
                  </span>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-4 pb-4">
              <button
                onClick={() => handleImagePromptResponse(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {t("vehicle.images.prompt_no")}
              </button>
              <button
                onClick={() => handleImagePromptResponse(true)}
                className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded hover:from-primary-700 hover:to-primary-800 active:from-primary-800 active:to-primary-900 transition-all duration-300"
              >
                {t("vehicle.images.prompt_yes")}
              </button>
            </div>

            <div className="border-t pt-4">
              {canHardDelete && (
                <>
                  <div className="text-sm text-gray-600 mb-2">
                    {t("vehicle.delete.prompt_timed", {
                      vehicle: localVehicle.plate,
                      seconds: deletionTimer,
                    })}
                  </div>
                  <button
                    onClick={handleDeleteVehicle}
                    disabled={deletionTimer === 0 || isDeletingVehicle}
                    className={`w-full px-4 py-2 text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors flex items-center justify-center gap-2 ${
                      deletionTimer === 0 || isDeletingVehicle
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isDeletingVehicle ? (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} />
                    )}
                    {t("vehicle.delete.button_hard")}
                  </button>
                </>
              )}
              {deletionTimer === 0 && isCreator && (
                <p className="text-xs text-center text-gray-500 mt-2">
                  {t("vehicle.delete.timer_expired")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deleted Vehicle Banner */}
      {localVehicle?.isDeleted && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 rounded-md flex items-center gap-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5" />
          <div>
            <p className="font-bold">{t("vehicle.status.deleted_title")}</p>
            <p className="text-sm">
              {t("vehicle.status.deleted_message", {
                date: formatDate(localVehicle.deletedAt),
              })}
            </p>
          </div>
          {canRestore && (
            <button
              onClick={handleRestoreVehicle}
              disabled={isRestoringVehicle}
              className="ml-auto px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
            >
              {isRestoringVehicle ? (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faUndo} />
              )}
              {t("vehicle.restore.button")}
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        <MetaTags
          title={`${vehicle.plate} - G80 Or Not?`}
          description={getMetaDescription()}
          image={getMetaImage()}
          url={window.location.href}
        />

        {/* Main Container */}
        <div
          className={`bg-white dark:bg-gray-800 rounded-lg border overflow-hidden ${
            localVehicle?.isDeleted ? "opacity-70" : ""
          } dark:text-gray-400 dark:border-gray-700`}
        >
          {/* Header Section */}
          <div className="py-4 px-4 border-b bg-gradient-to-b from-white to-gray-100 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700 dark:text-gray-400 dark:bg-gray-800">
            <div className="flex flex-col gap-3">
              {/* Top row: Plate and share buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PlateDisplay
                    plate={vehicle.plate}
                    plateStyle={vehicle.plateStyle}
                    isEditable={!!user}
                    canEditPlate={!!user && (isCreator || isAdmin)}
                    vehicleId={vehicle._id}
                    onStyleChange={(updatedVehicle) => {
                      setLocalVehicle(updatedVehicle);
                      vehicleState.setChangeHistory(
                        updatedVehicle.changeHistory
                      );
                    }}
                    onPlateChange={(updatedVehicle) => {
                      setLocalVehicle(updatedVehicle);
                      vehicleState.setChangeHistory(
                        updatedVehicle.changeHistory
                      );
                    }}
                  />
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleShare}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                      title={t("vehicle.share")}
                    >
                      <FontAwesomeIcon icon={faShare} className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={handleCopyLink}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                      title="Copy link"
                    >
                      <FontAwesomeIcon icon={faCopy} className="w-3.5 h-3.5" />
                    </button>

                    {canSoftDelete && !canHardDelete && (
                      <button
                        onClick={handleDeleteVehicle}
                        disabled={isDeletingVehicle}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                        title={t("vehicle.delete.button_soft")}
                      >
                        {isDeletingVehicle ? (
                          <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin h-3.5 w-3.5"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="w-3.5 h-3.5"
                          />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <UserAttributionPill
                  username={vehicle.firstAddedBy?.username}
                  profilePic={
                    vehicle.firstAddedBy?.profilePicVariants?.small ||
                    vehicle.firstAddedBy?.profilePic
                  }
                  date={vehicle.firstAddedAt}
                  type="spotted"
                />
              </div>

              {/* Middle row: Profile Richness Score */}
              <div className="flex items-center justify-between">
                <ProfileRichnessIndicator
                  score={vehicle.profileRichnessScore || 0}
                  size="md"
                  showLabel={true}
                  className="mt-1 mb-1"
                />
                {/* If we want to add a CTA button for incomplete profiles */}
                {vehicle.profileRichnessScore < 80 && user && (
                  <button
                    onClick={() =>
                      document
                        .getElementById("basic-info-section")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium dark:text-gray-400"
                  >
                    {t("profile.richness.improve_cta")}
                  </button>
                )}
              </div>

              {/* Bottom row: Stats */}
              <div className="flex items-center gap-4 text-xs text-gray-500 justify-end">
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faEye} className="w-3 h-3" />
                  <span>{vehicle.hits || 0} views</span>
                </div>
                <LikeButton vehicleId={vehicle._id} user={user} size="sm" />
              </div>
            </div>
          </div>

          {/* Disable content if deleted? Or just show banner? Currently dims content */}
          <fieldset
            disabled={localVehicle?.isDeleted}
            className="disabled:cursor-not-allowed"
          >
            {/* Vehicle Image Section */}
            <div className="border-b dark:border-gray-700">
              <VehicleImages
                vehicle={vehicle}
                user={user}
                imageState={imageState}
                onSubmitImage={handleSubmitImage}
              />
            </div>

            {/* Basic Information Section */}
            <div
              className="border-b dark:border-gray-700"
              id="basic-info-section"
            >
              <BasicInfo
                user={user}
                make={vehicleState.make}
                setMake={vehicleState.setMake}
                model={vehicleState.model}
                setModel={vehicleState.setModel}
                color={vehicleState.color}
                setColor={vehicleState.setColor}
                bodyStyle={vehicleState.bodyStyle}
                setBodyStyle={vehicleState.setBodyStyle}
                g80ConversionKit={vehicleState.g80ConversionKit}
                setG80ConversionKit={vehicleState.setG80ConversionKit}
                makes={vehicleState.makes}
                loadingMakes={vehicleState.loadingMakes}
                errorMakes={vehicleState.errorMakes}
                makeOptions={makeOptions}
                modelOptions={modelOptions}
                colorOptions={colorOptions}
                bodyStyleOptions={bodyStyleOptions}
                g80Options={g80Options}
                basicInfoMessage={vehicleState.basicInfoMessage}
                onSave={handleBasicInfoSave}
                t={t}
              />
            </div>

            {/* Profile Section */}
            <div className="border-b dark:border-gray-700">
              <ProfileSection
                user={user}
                profile={vehicleState.profile}
                setProfile={vehicleState.setProfile}
                profileMessage={vehicleState.profileMessage}
                onSave={handleProfileSave}
                t={t}
              />
            </div>

            {/* Tags Section */}
            <div className="p-4">
              <VehicleTags
                vehicle={localVehicle}
                user={user}
                onTagsUpdated={handleTagsUpdate}
              />
            </div>

            {/* Change History */}
            <div className="border-t dark:border-gray-700">
              <div className="p-4">
                <ChangeHistory
                  changeHistory={vehicleState.changeHistory}
                  t={t}
                />
              </div>
            </div>

            {/* Discussion Section */}
            <div className="border-t dark:border-gray-700">
              <div className="p-4">
                <DiscussionThread vehicleId={vehicle._id} user={user} />
              </div>
            </div>
          </fieldset>
        </div>
      </div>

      {scorePopup && (
        <ScoreChangePopup
          scoreChange={scorePopup.scoreChange}
          position={scorePopup.position}
        />
      )}
    </div>
  );
};

export default VehicleProfile;
